/**
 * Universal Markdown Parser
 * Works with any MD file format across all task types (features, user-stories, tech-stack, etc.)
 * Automatically detects structure and creates appropriate accordion format
 */

/**
 * Parse any markdown content into a universal accordion format
 * @param {string} markdownContent - The markdown content to parse
 * @param {string} taskType - Optional task type hint (features, user-stories, etc.)
 * @returns {Array} Array of categories with their items in universal format
 */
export const parseUniversalMarkdown = (markdownContent, taskType = null) => {
  if (!markdownContent) return [];

  console.log(`Parsing universal markdown for task: ${taskType || 'auto-detect'}`);

  const lines = markdownContent.split('\n');
  const categories = [];
  
  let currentCategory = null;
  let currentItem = null;
  let currentSection = null;
  let buffer = [];

  // Auto-detect task type from content if not provided
  const detectedTaskType = taskType || detectTaskType(markdownContent);
  console.log(`Detected/provided task type: ${detectedTaskType}`);

  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';

    // Skip empty lines at the beginning
    if (!line && !currentCategory && !currentItem) continue;

    // Level 1: Category headers (# Category)
    if (line.startsWith('# ')) {
      // Save any pending content
      if (currentItem && buffer.length > 0) {
        currentItem.content += '\n' + buffer.join('\n');
        buffer = [];
      }
      
      currentCategory = {
        title: line.substring(2).trim(),
        items: [],
        type: detectedTaskType
      };
      categories.push(currentCategory);
      currentItem = null;
      currentSection = null;
    }
    // Level 2: Main items (## Item) - Features, User Stories, etc.
    else if (line.startsWith('## ')) {
      // Save any pending content
      if (currentItem && buffer.length > 0) {
        currentItem.content += '\n' + buffer.join('\n');
        buffer = [];
      }

      // Create default category if none exists
      if (!currentCategory) {
        currentCategory = {
          title: getDefaultCategoryTitle(detectedTaskType),
          items: [],
          type: detectedTaskType
        };
        categories.push(currentCategory);
      }

      const itemTitle = line.substring(3).trim();
      const itemData = parseItemTitle(itemTitle, detectedTaskType);

      currentItem = {
        id: itemData.id || itemTitle,
        title: itemData.title || itemTitle,
        code: itemData.code,
        type: detectedTaskType,
        content: '',
        sections: {},
        metadata: {
          priority: null,
          complexity: null,
          status: null,
          tags: []
        }
      };
      
      currentCategory.items.push(currentItem);
      currentSection = null;
    }
    // Level 3: Subsections (### Subsection)
    else if (line.startsWith('### ')) {
      if (currentItem && buffer.length > 0) {
        if (currentSection) {
          currentItem.sections[currentSection] = buffer.join('\n');
        } else {
          currentItem.content += '\n' + buffer.join('\n');
        }
        buffer = [];
      }
      
      currentSection = line.substring(4).trim().toLowerCase();
    }
    // Bold sections (**Section:**)
    else if (line.match(/^\*\*([^*]+)\*\*:?\s*(.*)$/)) {
      if (currentItem && buffer.length > 0) {
        if (currentSection) {
          currentItem.sections[currentSection] = buffer.join('\n');
        } else {
          currentItem.content += '\n' + buffer.join('\n');
        }
        buffer = [];
      }

      const match = line.match(/^\*\*([^*]+)\*\*:?\s*(.*)$/);
      const sectionName = match[1].toLowerCase();
      const sectionContent = match[2];

      if (currentItem) {
        currentSection = sectionName;
        
        // Handle metadata sections
        if (isMetadataField(sectionName)) {
          setMetadataField(currentItem.metadata, sectionName, sectionContent);
        }
        
        if (sectionContent) {
          buffer.push(sectionContent);
        }
      }
    }
    // Regular content lines
    else if (line) {
      buffer.push(line);
    }
    // Empty line - potential section break
    else {
      if (buffer.length > 0 && currentItem) {
        if (currentSection) {
          currentItem.sections[currentSection] = (currentItem.sections[currentSection] || '') + '\n' + buffer.join('\n');
        } else {
          currentItem.content += '\n' + buffer.join('\n');
        }
        buffer = [];
      }
    }
  }

  // Save any remaining buffered content
  if (currentItem && buffer.length > 0) {
    if (currentSection) {
      currentItem.sections[currentSection] = buffer.join('\n');
    } else {
      currentItem.content += '\n' + buffer.join('\n');
    }
  }

  // If no categories were created, create a default one with all content
  if (categories.length === 0 && markdownContent.trim()) {
    categories.push({
      title: getDefaultCategoryTitle(detectedTaskType),
      items: [{
        id: 'Content',
        title: 'Imported Content',
        type: detectedTaskType,
        content: markdownContent,
        sections: {},
        metadata: {}
      }],
      type: detectedTaskType
    });
  }

  console.log(`Parsed ${categories.length} categories with ${categories.reduce((sum, cat) => sum + cat.items.length, 0)} total items`);
  return categories;
};

/**
 * Auto-detect task type from content
 */
const detectTaskType = (content) => {
  const lower = content.toLowerCase();
  
  // Check for specific patterns in order of specificity
  // Look for explicit task identifiers first
  if (lower.includes('us-') || lower.includes('user story') || lower.includes('as a ') || lower.includes('i want') || lower.includes('so that')) {
    return 'user-stories';
  }
  if (lower.includes('f001:') || lower.includes('f002:') || lower.includes('feature')) {
    return 'features';
  }
  if (lower.includes('wireframe') || lower.includes('mockup') || lower.includes('ui ') || lower.includes('interface')) {
    return 'wireframes';
  }
  if (lower.includes('architecture') || lower.includes('system design') || lower.includes('component') || lower.includes('service')) {
    return 'architecture';
  }
  if (lower.includes('technology') || lower.includes('framework') || lower.includes('library') || lower.includes('database')) {
    return 'tech-stack';
  }
  if (lower.includes('role') || lower.includes('permission') || lower.includes('access') || lower.includes('admin')) {
    return 'user-roles';
  }
  if (lower.includes('process') || lower.includes('workflow') || lower.includes('flow') || lower.includes('step')) {
    return 'process-flows';
  }
  if (lower.includes('security') || lower.includes('authentication') || lower.includes('authorization')) {
    return 'security-guidelines';
  }
  if (lower.includes('test') || lower.includes('testing') || lower.includes('unit test')) {
    return 'unit-testing';
  }
  if (lower.includes('api') || lower.includes('endpoint') || lower.includes('rest') || lower.includes('graphql')) {
    return 'api-guidelines';
  }
  if (lower.includes('validation') || lower.includes('field') || lower.includes('form')) {
    return 'field-validations';
  }
  
  // Default to features if no specific type detected
  return 'features';
};

/**
 * Get default category title based on task type
 */
const getDefaultCategoryTitle = (taskType) => {
  const titles = {
    'features': 'Features',
    'user-stories': 'User Stories',
    'user-roles': 'User Roles',
    'wireframes': 'Wireframes',
    'architecture': 'Architecture',
    'tech-stack': 'Technology Stack',
    'process-flows': 'Process Flows',
    'security-guidelines': 'Security Guidelines',
    'unit-testing': 'Unit Testing',
    'api-guidelines': 'API Guidelines',
    'field-validations': 'Field Validations'
  };
  return titles[taskType] || 'Content';
};

/**
 * Parse item title to extract ID, code, and clean title
 */
const parseItemTitle = (title, taskType) => {
  // Pattern for F001: Title format
  let match = title.match(/^([A-Za-z]+\d+):\s*(.+)$/);
  if (match) {
    return {
      id: title, // Full title including code
      code: match[1], // F001
      title: match[2] // Clean title
    };
  }

  // Pattern for US-001: Title format (user stories)
  match = title.match(/^([A-Za-z]+-\d+):\s*(.+)$/);
  if (match) {
    return {
      id: title,
      code: match[1],
      title: match[2]
    };
  }

  // No pattern found, use as-is
  return {
    id: title,
    code: null,
    title: title
  };
};

/**
 * Check if field is metadata
 */
const isMetadataField = (fieldName) => {
  const metadataFields = [
    'priority', 'complexity', 'status', 'effort', 'points', 
    'assignee', 'epic', 'sprint', 'due date', 'category',
    'type', 'severity', 'impact'
  ];
  return metadataFields.includes(fieldName.toLowerCase());
};

/**
 * Set metadata field value
 */
const setMetadataField = (metadata, fieldName, value) => {
  const field = fieldName.toLowerCase();
  switch (field) {
    case 'priority':
      metadata.priority = value;
      break;
    case 'complexity':
    case 'effort':
      metadata.complexity = value;
      break;
    case 'status':
      metadata.status = value;
      break;
    default:
      metadata[field] = value;
  }
};

/**
 * Check if content is valid markdown for import
 */
export const isValidMarkdown = (content) => {
  if (!content || typeof content !== 'string') return false;
  
  // Must have some structure (headings, bold sections, or lists)
  return !!(
    content.match(/#+ /) || // Headers
    content.match(/^\*\*[^*]+\*\*:?/m) || // Bold sections
    content.match(/^[-*]\s+/m) || // Lists
    content.length > 50 // At least some substantial content
  );
};

export default {
  parseUniversalMarkdown,
  isValidMarkdown,
  detectTaskType: (content) => detectTaskType(content)
};